import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cars } from '../data/cars';
import { Users, Fuel, Settings2, Calendar, ArrowLeft, Check, Phone, Gauge } from 'lucide-react';
import './CarDetail.css';

function ImageZoom({ src, alt }) {
  const [zoomed, setZoomed] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const handleMove = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x, y });
  };

  return (
    <div
      className={`zoom-wrap ${zoomed ? 'zoom-wrap--active' : ''}`}
      onMouseEnter={() => setZoomed(true)}
      onMouseLeave={() => setZoomed(false)}
      onMouseMove={handleMove}
      ref={imgRef}
    >
      <img
        src={src}
        alt={alt}
        className="zoom-img"
        style={zoomed ? {
          transformOrigin: `${pos.x}% ${pos.y}%`,
          transform: 'scale(2.5)',
        } : {}}
      />
    </div>
  );
}

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const car = cars.find(c => c.id === Number(id));
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  if (!car) return (
    <div className="detail-notfound">
      <p>Makina nuk u gjet.</p>
      <button className="detail-notfound__btn" onClick={() => navigate('/')}>
        <ArrowLeft size={16} /> Kthehu
      </button>
    </div>
  );

  const allPhotos = [car.image, ...(car.photos || [])].filter(Boolean);

  const goHome = (section) => {
    navigate('/');
    setTimeout(() => {
      if (section) {
        const el = document.getElementById(section);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 100);
  };

  const handleWhatsApp = () => {
    const text = `Pershendetje! 👋\n\nDua të rezervoj: ${car.name} (${car.year})\nÇmimi: €${car.pricePerDay}/ditë\n\nJu lutem më kontaktoni për disponueshmërinë.`;
    window.open(`https://wa.me/38343888408?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className="detail">

      <div className="detail__bar">
        <div className="container detail__bar-inner">
          <button className="detail__back" onClick={() => goHome(null)}>
            <ArrowLeft size={15} />
            <span>Kthehu</span>
          </button>
          <span className={`detail__status ${car.available ? 'detail__status--on' : 'detail__status--off'}`}>
            {car.available ? '● E Lirë' : '● E Zënë'}
          </span>
        </div>
      </div>

      <div className="container detail__body">

        {/* GALLERY */}
        <div className="detail__gallery">

          <div className="detail__main-photo">
            {allPhotos.length > 0 && allPhotos[activePhoto] ? (
              <ImageZoom src={allPhotos[activePhoto]} alt={car.name} />
            ) : (
              <div className="detail__placeholder">
                <span>{car.name[0]}</span>
              </div>
            )}
            <span className="detail__cat-badge">{car.category}</span>
          </div>

          {allPhotos.length > 1 && (
            <div className="detail__thumbs">
              {allPhotos.map((photo, i) => (
                <button
                  key={i}
                  onClick={() => setActivePhoto(i)}
                  className={`detail__thumb ${activePhoto === i ? 'detail__thumb--active' : ''}`}
                >
                  {photo
                    ? <img src={photo} alt={`${car.name} ${i + 1}`} />
                    : <div className="detail__thumb-empty" />
                  }
                </button>
              ))}
            </div>
          )}

        </div>

        {/* INFO */}
        <div className="detail__info">

          <div className="detail__head">
            <span className="detail__eyebrow">{car.year} · {car.category}</span>
            <h1 className="detail__name">{car.name}</h1>
          </div>

          <p className="detail__desc">{car.description}</p>

          <div className="detail__specs">
  <div className="detail__spec">
    <Users size={15} />
    <div>
      <span>Ulëse</span>
      <strong>{car.seats}</strong>
    </div>
  </div>
  <div className="detail__spec">
    <Settings2 size={15} />
    <div>
      <span>Transmisioni</span>
      <strong>{car.transmission}</strong>
    </div>
  </div>
  <div className="detail__spec">
    <Fuel size={15} />
    <div>
      <span>Karburanti</span>
      <strong>{car.fuel}</strong>
    </div>
  </div>
  <div className="detail__spec">
    <Calendar size={15} />
    <div>
      <span>Viti</span>
      <strong>{car.year}</strong>
    </div>
  </div>
  {car.engine && (
    <div className="detail__spec">
      <Gauge size={15} />
      <div>
        <span>Motori</span>
        <strong>{car.engine}</strong>
      </div>
    </div>
  )}
</div>

          {car.features && car.features.length > 0 && (
            <div className="detail__features">
              <h3 className="detail__features-label">Pajisjet</h3>
              <ul className="detail__features-list">
                {car.features.map((f, i) => (
                  <li key={i}>
                    <Check size={12} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="detail__actions">
            {car.available ? (
              <button className="detail__btn detail__btn--primary" onClick={handleWhatsApp}>
                <Phone size={15} />
                Rezervo në WhatsApp
              </button>
            ) : (
              <div className="detail__btn detail__btn--disabled">
                Nuk Disponohet
              </div>
            )}
            <button className="detail__btn detail__btn--ghost" onClick={() => goHome('fleet')}>
              Shiko Makina të Tjera
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}